"use client";

import { Link } from "@/i18n/navigation";
import { Button } from "@/components/ui/button";
import { Home, Phone, Search, Loader2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { Navbar } from "@/components/layout/navbar";
import { Footer } from "@/components/layout/footer";

export default function NotFound() {
  const t = useTranslations("NotFound");
  const c = useTranslations("Common");

  return (
    <div className="flex flex-col min-h-screen bg-[#fafbfc] dark:bg-[#020617] text-slate-900 dark:text-slate-50 overflow-x-hidden selection:bg-primary/20">
      <Navbar />
      
      <main className="flex-grow relative flex flex-col items-center justify-center pt-32 pb-20 px-6">
        {/* Soft Background Glows */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[600px] bg-gradient-to-b from-primary/5 to-transparent pointer-events-none" />
        
        <div className="container max-w-5xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            
            {/* Visual Side */}
            <div 
               className="relative order-2 lg:order-1"
            >
               <div className="relative inline-block w-full max-w-[400px]">
                  {/* Decorative Circle */}
                  <div className="absolute inset-0 bg-primary/5 rounded-full blur-3xl animate-pulse" />
                  
                  {/* Large 404 Text */}
                  <h1 className="text-[180px] md:text-[240px] font-black leading-none tracking-tighter text-slate-200/50 dark:text-slate-800/30 select-none">
                     404
                  </h1>
                  
                  {/* Diagnostic Card Overlay */}
                  <div 
                     className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full glass-card p-10 rounded-[2.5rem] border-slate-200 dark:border-white/10 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.1)] flex flex-col items-center gap-6"
                   >
                     <div className="w-20 h-20 bg-primary text-white rounded-3xl flex items-center justify-center shadow-xl shadow-primary/20">
                        <Search className="w-10 h-10" />
                     </div>
                     <div className="flex items-center gap-3 py-2 px-5 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 rounded-full text-xs font-bold tracking-widest uppercase">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {t("scanning")}
                     </div>
                  </div>
               </div>
            </div>

            {/* Content Side */}
            <div 
               className="text-center lg:text-start space-y-8 order-1 lg:order-2"
            >
               <div className="space-y-4">
                  <h2 className="text-4xl md:text-6xl font-black font-heading tracking-tight leading-tight">
                    {t("title")}
                  </h2>
                  <p className="text-xl text-slate-500 dark:text-slate-400 leading-relaxed max-w-md">
                    {t("description")}
                  </p>
               </div>

               <div className="flex flex-col sm:flex-row gap-4 items-center lg:items-start">
                  <Button size="lg" asChild className="h-16 px-10 text-lg font-bold shadow-2xl hover:scale-105 active:scale-95 transition-all rounded-2xl group">
                      <Link href="/">
                          <Home className="me-2 w-5 h-5" /> {t("back_home")}
                      </Link>
                  </Button>
                  <Button size="lg" variant="outline" className="h-16 px-10 text-lg font-bold border-slate-200 dark:border-white/10 bg-white/50 dark:bg-white/5 hover:bg-slate-50 dark:hover:bg-white/10 backdrop-blur-md active:scale-95 transition-all rounded-2xl" asChild>
                      <Link href="/#contact">
                          <Phone className="me-2 w-5 h-5 text-primary" /> {c("contact")}
                      </Link>
                  </Button>
               </div>

               {/* Corporate Badge */}
               <div className="pt-10 flex items-center justify-center lg:justify-start gap-4 opacity-40">
                  <div className="h-px w-12 bg-slate-300 dark:bg-slate-700" />
                  <span className="text-xs font-black tracking-[4px] uppercase italic">
                    {t("systems")}
                  </span>
               </div>
            </div>

          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
